/**
 * The privacy policy and terms of use, shown together on the /privacy page and
 * linked from the bot and the Mini App card.
 *
 * The texts live in legalTexts.ts, generated from docs/legal in the workspace
 * root so that all four apps share one wording. They exist in Russian and
 * English only: a legal text is not something to machine-translate. Every
 * other UI language gets the English version with a notice above it.
 */

import { privacyEn, privacyRu, termsEn, termsRu } from "./legalTexts";

export type PolicySection = { heading: string; paragraphs: string[] };

export type PrivacyPolicy = {
  title: string;
  updated: string;
  intro: string[];
  sections: PolicySection[];
};

export const PRIVACY_UPDATED_ISO = "2026-09-23";

const isRussian = (language: string | undefined) => !!language?.toLowerCase().startsWith("ru");

/** Russian for Russian-speaking clients, English for everyone else. */
export function privacyPolicyFor(language: string | undefined): PrivacyPolicy {
  return isRussian(language) ? privacyRu : privacyEn;
}

/** Same language rule as the privacy policy; shown below it on the same page. */
export function termsOfUseFor(language: string | undefined): PrivacyPolicy {
  return isRussian(language) ? termsRu : termsEn;
}

/**
 * Shown above the full document when it is not in the reader's language.
 *
 * The summary above it is translated into every UI language, so the switch to
 * English used to arrive without warning halfway down the page. Saying it
 * plainly is the honest version until the legal text itself is translated.
 */
const FULL_TEXT_NOTICE: Record<string, string> = {
  en: '',
  ru: '',
  am: 'ሙሉው የግላዊነት ፖሊሲ እና የአጠቃቀም ውሎች በእንግሊዝኛ ብቻ ይገኛሉ።',
  ar: 'النص الكامل لسياسة الخصوصية وشروط الاستخدام متاح بالإنجليزية فقط.',
  bn: 'সম্পূর্ণ গোপনীয়তা নীতি ও ব্যবহারের শর্তাবলি কেবল ইংরেজিতে পাওয়া যায়।',
  fa: 'متن کامل سیاست حریم خصوصی و شرایط استفاده فقط به انگلیسی در دسترس است.',
  fr: 'Le texte complet de la politique de confidentialité et des conditions d’utilisation n’existe qu’en anglais.',
  hi: 'पूरी गोपनीयता नीति और उपयोग की शर्तें केवल अंग्रेज़ी में उपलब्ध हैं।',
  id: 'Teks lengkap kebijakan privasi dan ketentuan penggunaan hanya tersedia dalam bahasa Inggris.',
  ko: '개인정보 처리방침과 이용약관 전문은 영어로만 제공됩니다.',
  pt: 'O texto completo da política de privacidade e dos termos de uso está disponível apenas em inglês.',
  zh: '隐私政策和使用条款全文仅提供英文版本。',
};

/** Empty when the document below is already in the reader's language. */
export function fullPolicyNotice(language: string | undefined): string {
  const base = (language ?? '').toLowerCase().split(/[-_]/)[0];
  return FULL_TEXT_NOTICE[base] ?? FULL_TEXT_NOTICE.en;
}
